import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2 } from "lucide-react";

export default function Reschedule() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [slotDate, setSlotDate] = useState("");
  const [slotTime, setSlotTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/reschedule/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(apiError(e.response?.data?.detail) || "This link is invalid or has expired"));
  }, [token]);

  const timesForDate = info?.slots?.find((s) => s.date === slotDate)?.times || [];

  const confirm = async () => {
    if (!slotDate || !slotTime) return toast.error("Pick a new day and time");
    setBusy(true);
    try {
      const { data } = await api.post(`/reschedule/${token}`, { scheduled_at: `${slotDate}T${slotTime}:00` });
      setDone(data.scheduled_at);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not reschedule");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-14">
      <div className="card-metal p-8" data-testid="reschedule-card">
        <h1 className="font-heading font-black text-2xl text-slate-900 flex items-center gap-2">
          <CalendarClock className="w-6 h-6 text-[#9b111e]" /> Reschedule your visit
        </h1>
        {error ? (
          <p className="text-sm text-red-600 mt-4" data-testid="reschedule-error">{error}</p>
        ) : done ? (
          <div className="mt-4" data-testid="reschedule-success">
            <p className="text-lg font-bold text-green-700 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" /> You're all set!
            </p>
            <p className="text-sm text-slate-600 mt-2">
              Your <b>{info?.booking?.service}</b> with <b>{info?.booking?.provider_name}</b> is now on{" "}
              <b>{new Date(done).toLocaleString()}</b>. See you then! 💛
            </p>
          </div>
        ) : !info ? (
          <p className="text-slate-400 mt-4">Loading your visit…</p>
        ) : (
          <div className="mt-4 space-y-4">
            <p className="text-sm text-slate-600" data-testid="reschedule-summary">
              🔁 Your recurring <b>{info.booking.service}</b> with <b>{info.booking.provider_name}</b> is currently
              set for <b>{new Date(info.booking.scheduled_at).toLocaleString()}</b>. Pick any open slot below — no
              login needed.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>New day</Label>
                <Select value={slotDate} onValueChange={(v) => { setSlotDate(v); setSlotTime(""); }}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="reschedule-date">
                    <SelectValue placeholder={info.slots.length ? "Pick a day" : "No openings"} />
                  </SelectTrigger>
                  <SelectContent>
                    {info.slots.map((s) => <SelectItem key={s.date} value={s.date}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>New time</Label>
                <Select value={slotTime} onValueChange={setSlotTime} disabled={!slotDate}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="reschedule-time">
                    <SelectValue placeholder={slotDate ? "Pick a time" : "Pick a day first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {timesForDate.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <button onClick={confirm} disabled={busy} className="btn-red-3d w-full" data-testid="reschedule-confirm-btn">
              {busy ? "Saving…" : "Confirm new time"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
